import classNames from "classnames";
import { Separator } from "components/Separator";
import AnimalStudyTag from "components/Tag/tags/AnimalTrialTag";
import CaseStudyTag from "components/Tag/tags/CaseStudyTag";
import DisputedPaperTag from "components/Tag/tags/DisputedPaperTag";
import HighlyCitedTag from "components/Tag/tags/HighlyCitedTag";
import InVitroStudyTag from "components/Tag/tags/InVitroTrialTag";
import LargeHumanTrialTag from "components/Tag/tags/LargeHumanTrialTag";
import LiteratureReviewTag from "components/Tag/tags/LiteratureReviewTag";
import MetaAnalysisTag from "components/Tag/tags/MetaAnalysisTag";
import NonRctTag from "components/Tag/tags/NonRctTrialTag";
import ObservationalStudyTag from "components/Tag/tags/ObservationalStudyTag";
import PreprintTag from "components/Tag/tags/PreprintTag";
import RctTag from "components/Tag/tags/RctTag";
import RigorousJournalTag from "components/Tag/tags/RigorousJournalTag";
import SystematicReviewTag from "components/Tag/tags/SystematicReviewTag";
import VeryRigourousJournalTag from "components/Tag/tags/VeryRigorousJournalTag";
import { LibraryIcon } from "features/libraries";
import { Badges, StudyType } from "helpers/api";
import { ReactNode } from "react";

type BadgesListProps = {
  badges?: Badges;
  extraTags?: ReactNode[];
  isCompact?: boolean;
  includeSeparator?: boolean;
  includeLibraryIcon?: boolean;
};

function BadgesList({
  badges,
  extraTags = [],
  isCompact = false,
  includeSeparator = false,
  includeLibraryIcon = false,
}: BadgesListProps) {
  // boolean badge mappings
  const badgesMap: Partial<Record<keyof Badges, ReactNode>> = {
    animal_trial: <AnimalStudyTag key="animal_trial" isCompact={isCompact} />,
    large_human_trial: (
      <LargeHumanTrialTag key="large_human_trial" isCompact={isCompact} />
    ),
    very_rigorous_journal: (
      <VeryRigourousJournalTag
        key="very_rigorous_journal"
        isCompact={isCompact}
      />
    ),
    rigorous_journal: (
      <RigorousJournalTag key="rigorous_journal" isCompact={isCompact} />
    ),
    highly_cited_paper: (
      <HighlyCitedTag key="highly_cited_paper" isCompact={isCompact} />
    ),
    preprint: <PreprintTag key="preprint" isCompact={isCompact} />,
  };

  // Badges by StudyType
  const studyTypeBadgesMap: Record<StudyType, ReactNode> = {
    [StudyType.RCT]: <RctTag key="rct" isCompact={isCompact} />,
    [StudyType.ANIMAL_STUDY]: (
      <AnimalStudyTag key="animal_trial" isCompact={isCompact} />
    ),
    [StudyType.META_ANALYSIS]: (
      <MetaAnalysisTag key="meta_analysis" isCompact={isCompact} />
    ),
    [StudyType.SYSTEMATIC_REVIEW]: (
      <SystematicReviewTag key="systematic_review" isCompact={isCompact} />
    ),
    [StudyType.CASE_STUDY]: (
      <CaseStudyTag key="case_study" isCompact={isCompact} />
    ),
    [StudyType.NON_RCT_TRIAL]: (
      <NonRctTag key="non_rct_trial" isCompact={isCompact} />
    ),
    [StudyType.OBSERVATIONAL_STUDY]: (
      <ObservationalStudyTag key="observational_study" isCompact={isCompact} />
    ),
    [StudyType.LITERATURE_REVIEW]: (
      <LiteratureReviewTag key="literature_review" isCompact={isCompact} />
    ),
    [StudyType.IN_VITRO_TRIAL]: (
      <InVitroStudyTag key="in_vitro_trial" isCompact={isCompact} />
    ),
  };

  const badgeTags: ReactNode[] = [];

  if (includeLibraryIcon) {
    badgeTags.push(<LibraryIcon />);
  }

  if (
    badges &&
    badges.disputed &&
    badges.disputed.reason &&
    badges.disputed.url
  ) {
    badgeTags.push(
      <DisputedPaperTag
        key="disputed"
        reason={badges.disputed.reason}
        url={badges.disputed.url}
        isCompact={isCompact}
      />
    );
  }

  // Add StudyType badge if a matching type exists in the map
  if (badges && badges.study_type && studyTypeBadgesMap[badges.study_type]) {
    badgeTags.push(studyTypeBadgesMap[badges.study_type]);
  }

  // Add tags for boolean-based badges
  Object.entries(badgesMap).forEach(([key, component]) => {
    if (badges && badges[key as keyof Badges]) {
      badgeTags.push(component);
    }
  });

  // Append any extraTags provided
  badgeTags.push(...extraTags);

  if (badgeTags.length === 0) return null;
  return (
    <>
      {includeSeparator && <Separator />}
      <div
        className={classNames(
          "inline-flex flex-wrap",
          isCompact ? "" : "gap-3"
        )}
        onClick={(e) => {
          // Clicking on badges should not trigger a click on the overall search result
          e.stopPropagation();
        }}
      >
        {badgeTags}
      </div>
    </>
  );
}

export default BadgesList;
